import {
    blurField,
    distanceTo,
    fromWorld,
    loadBuildings,
    loadMapFeatures,
    morph,
} from '@/components/Editor/WoodMap/mapTiles';

// raster DEM: every pixel is a height, so layers can't leak through tile seams like clipped contour lines did
const DEM_URL = 'https://s3.amazonaws.com/elevation-tiles-prod/terrarium';
const DEM_TILE = 256;
const MAX_DEM_ZOOM = 15;
export const DEM_WIDTH = 768;
// pieces below 0.5% of the land are dropped (measured: about half the pieces of 0.2%, same outlines)
const MIN_LAYER_AREA = 0.005;
// gaps narrower than 2×GAP_PX (~2 mm on a 40 cm board) are bridged so neighbouring hills cut as one piece
const GAP_PX = 4;
// rounds off the creases bathymetry bands get where two shores' distances meet
const SHORE_BLUR_PX = 6;
// zoomed out, country-wide views drop up to 3.5× bigger specks (the gap stays: a bigger square kernel makes blocky edges)
const SIMPLIFY_FULL_ZOOM = 10;
const SIMPLIFY_MAX = 3.5;
const LOCATOR_LINES = 12;
// the tallest 2% of buildings share the top ply, so one tower doesn't flatten the rest of the city
const CITY_TOP_QUANTILE = 0.98;
// about 2×2 mm on a 40 cm board
const MIN_BUILDING_PX = 24;
// Mapbox Streets keeps only a few landmark buildings below z15 tiles (central Budapest: 3 at z13, 150 at z15)
const BUILDING_TILE_ZOOM = 15;
// Mapbox Streets has no river lines below z7, country views borrow them from z7 tiles
const RIVER_ZOOM = 7;
const EARTH_METRES = 40075016.686;
// typical real widths; rivers keep a readable minimum, canals vanish once thinner than a cut
const WATERWAY_METRES = { river: 120, canal: 25 };
const MIN_RIVER_PX = 2;
const MIN_CANAL_PX = 1.5;

export function waterwayWidthPx(kind, metresPerPx) {
    const px = (WATERWAY_METRES[kind] || 0) / metresPerPx;
    if (kind === 'river') return Math.max(MIN_RIVER_PX, px);
    return px >= MIN_CANAL_PX ? px : 0;
}

function project(view, width, height) {
    const scaleX = width / (view.right - view.left);
    const scaleY = height / (view.bottom - view.top);
    return (x, y) => [(x - view.left) * scaleX, (y - view.top) * scaleY];
}

export const decodeHeight = (r, g, b) => r * 256 + g + b / 256 - 32768;

// smallest 1/2/2.5/5 × 10^n step that splits `range` into at most `count` bands
export function niceStep(range, count) {
    const raw = range / Math.max(1, count);
    if (!(raw > 0)) return 0;
    const base = 10 ** Math.floor(Math.log10(raw));
    return [1, 2, 2.5, 5, 10].map((m) => m * base).find((step) => step >= raw);
}

export function levelsFor(min, max, count) {
    if (!(max > min) || count < 1) return [];
    const step = (max - min) / (count + 1);
    const levels = [];
    for (let i = 1; i <= count; i++) levels.push(min + i * step);
    return levels;
}

// 1 at city zoom, growing by 0.5 per zoom level out, capped
export function simplifyFactor(zoom) {
    return Math.min(SIMPLIFY_MAX, Math.max(1, 1 + (SIMPLIFY_FULL_ZOOM - zoom) * 0.5));
}

// smallest zoom whose DEM pixels are at least as dense as the output grid
export function demZoomFor(view, width) {
    const z = Math.ceil(Math.log2(width / ((view.right - view.left) * DEM_TILE)));
    return Math.max(0, Math.min(MAX_DEM_ZOOM, z));
}

// shortcut: unbounded cache (~1 MB per tile), add LRU eviction if long sessions get heavy
const tileCache = new Map();

function loadDemTile(tile) {
    const key = `${tile.z}/${tile.x}/${tile.y}`;
    if (!tileCache.has(key)) {
        const request = fetch(`${DEM_URL}/${key}.png`)
            .then(async (response) => {
                if (response.status === 404) return null;
                if (!response.ok) throw new Error(`Elevation tile error ${response.status}`);
                const bitmap = await createImageBitmap(await response.blob(), {
                    colorSpaceConversion: 'none',
                    premultiplyAlpha: 'none',
                });
                const canvas = document.createElement('canvas');
                canvas.width = DEM_TILE;
                canvas.height = DEM_TILE;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(bitmap, 0, 0, DEM_TILE, DEM_TILE);
                const px = ctx.getImageData(0, 0, DEM_TILE, DEM_TILE).data;
                const heights = new Float32Array(DEM_TILE * DEM_TILE);
                for (let i = 0; i < heights.length; i++)
                    heights[i] = decodeHeight(px[i * 4], px[i * 4 + 1], px[i * 4 + 2]);
                return heights;
            })
            .catch((error) => {
                tileCache.delete(key);
                throw error;
            });
        tileCache.set(key, request);
    }
    return tileCache.get(key);
}

// bilinear resample of the stitched DEM tiles onto the width×height view grid
function sampleHeights(tiles, grids, view, width, height) {
    const z = tiles[0].z;
    const x0 = Math.min(...tiles.map((t) => t.x));
    const y0 = Math.min(...tiles.map((t) => t.y));
    const byKey = new Map(tiles.map((t, i) => [`${t.x - x0}/${t.y - y0}`, grids[i]]));
    const world = 2 ** z * DEM_TILE;
    const at = (gx, gy) => {
        const tx = Math.floor(gx / DEM_TILE);
        const ty = Math.floor(gy / DEM_TILE);
        const grid = byKey.get(`${tx}/${ty}`);
        if (!grid) return 0;
        return grid[(gy - ty * DEM_TILE) * DEM_TILE + (gx - tx * DEM_TILE)];
    };
    const maxX = (Math.max(...tiles.map((t) => t.x)) - x0 + 1) * DEM_TILE - 1;
    const maxY = (Math.max(...tiles.map((t) => t.y)) - y0 + 1) * DEM_TILE - 1;
    const out = new Float32Array(width * height);
    for (let py = 0; py < height; py++) {
        const gy = Math.min(
            maxY,
            Math.max(0, (view.top + ((py + 0.5) / height) * (view.bottom - view.top)) * world - y0 * DEM_TILE - 0.5)
        );
        const iy = Math.min(maxY - 1, Math.floor(gy));
        const fy = gy - iy;
        for (let px = 0; px < width; px++) {
            const gx = Math.min(
                maxX,
                Math.max(0, (view.left + ((px + 0.5) / width) * (view.right - view.left)) * world - x0 * DEM_TILE - 0.5)
            );
            const ix = Math.min(maxX - 1, Math.floor(gx));
            const fx = gx - ix;
            const top = at(ix, iy) * (1 - fx) + at(ix + 1, iy) * fx;
            const bottom = at(ix, iy + 1) * (1 - fx) + at(ix + 1, iy + 1) * fx;
            out[py * width + px] = top * (1 - fy) + bottom * fy;
        }
    }
    return out;
}

function paintWater(features, view, width, height) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    const toPx = project(view, width, height);
    const lat = fromWorld(0, (view.top + view.bottom) / 2).lat;
    const metresPerPx = ((view.right - view.left) * EARTH_METRES * Math.cos((lat * Math.PI) / 180)) / width;
    ctx.fillStyle = '#fff';
    ctx.strokeStyle = '#fff';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    features.forEach((feature) => {
        const trace = () =>
            feature.rings.forEach((ring) =>
                ring.forEach((point, index) => {
                    const [px, py] = toPx(point[0], point[1]);
                    if (index) ctx.lineTo(px, py);
                    else ctx.moveTo(px, py);
                })
            );
        if (feature.water) {
            ctx.beginPath();
            trace();
            ctx.fill('evenodd');
        } else if (feature.river) {
            const lineWidth = waterwayWidthPx(feature.waterway, metresPerPx);
            if (!lineWidth) return;
            ctx.lineWidth = lineWidth;
            ctx.beginPath();
            trace();
            ctx.stroke();
        }
    });
    const pixels = ctx.getImageData(0, 0, width, height).data;
    const water = new Uint8Array(width * height);
    for (let i = 0; i < water.length; i++) water[i] = pixels[i * 4 + 3] > 16 ? 1 : 0;
    return water;
}

function maskFromBits(width, height, bits) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    const image = ctx.createImageData(width, height);
    const data = image.data;
    for (let i = 0; i < bits.length; i++) {
        if (!bits[i]) continue;
        const o = i * 4;
        data[o] = data[o + 1] = data[o + 2] = data[o + 3] = 255;
    }
    ctx.putImageData(image, 0, 0);
    const smooth = document.createElement('canvas');
    smooth.width = width;
    smooth.height = height;
    const smoothCtx = smooth.getContext('2d');
    smoothCtx.filter = 'blur(1.6px)';
    smoothCtx.drawImage(canvas, 0, 0);
    const cleaned = smoothCtx.getImageData(0, 0, width, height);
    const out = cleaned.data;
    for (let i = 0; i < out.length; i += 4) {
        const on = out[i + 3] > 132;
        out[i] = out[i + 1] = out[i + 2] = on ? 255 : 0;
        out[i + 3] = 255;
    }
    smoothCtx.putImageData(cleaned, 0, 0);
    return smooth;
}

function landBits(water) {
    const land = new Uint8Array(water.length);
    for (let i = 0; i < water.length; i++) land[i] = water[i] ? 0 : 1;
    return land;
}

function areaOf(bits) {
    let area = 0;
    for (let i = 0; i < bits.length; i++) area += bits[i];
    return area;
}

export function countParts(bits, width, height, minArea = 1) {
    const seen = new Uint8Array(bits.length);
    const queue = [];
    let parts = 0;
    for (let start = 0; start < bits.length; start++) {
        if (!bits[start] || seen[start]) continue;
        queue.length = 0;
        queue.push(start);
        seen[start] = 1;
        let area = 1;
        let head = 0;
        while (head < queue.length) {
            const i = queue[head++];
            const x = i % width;
            const y = (i - x) / width;
            const next = [x > 0 && i - 1, x + 1 < width && i + 1, y > 0 && i - width, y + 1 < height && i + width];
            next.forEach((n) => {
                if (n === false || !bits[n] || seen[n]) return;
                seen[n] = 1;
                queue.push(n);
                area++;
            });
        }
        if (area >= minArea) parts++;
    }
    return parts;
}

function removeSpecks(bits, width, height, minArea) {
    const seen = new Uint8Array(bits.length);
    const kept = new Uint8Array(bits.length);
    const queue = [];
    for (let start = 0; start < bits.length; start++) {
        if (!bits[start] || seen[start]) continue;
        queue.length = 0;
        queue.push(start);
        seen[start] = 1;
        const component = [start];
        let head = 0;
        while (head < queue.length) {
            const i = queue[head++];
            const x = i % width;
            const y = (i - x) / width;
            const next = [x > 0 && i - 1, x + 1 < width && i + 1, y > 0 && i - width, y + 1 < height && i + width];
            next.forEach((n) => {
                if (n === false || !bits[n] || seen[n]) return;
                seen[n] = 1;
                queue.push(n);
                component.push(n);
            });
        }
        if (component.length >= minArea)
            component.forEach((i) => {
                kept[i] = 1;
            });
    }
    return kept;
}

// fewer, sturdier pieces to cut and glue: bridge narrow gaps, drop specks, fill pinholes
export function mergePieces(bits, width, height, minArea, below) {
    const closed = morph(morph(bits, width, height, GAP_PX, true), width, height, GAP_PX, false);
    const kept = removeSpecks(closed, width, height, minArea);
    const holes = removeSpecks(
        kept.map((v) => 1 - v),
        width,
        height,
        minArea
    );
    // a ply never overhangs the one below it
    return kept.map((v, i) => (!holes[i] && below[i] ? 1 : 0));
}

// white 2px outline of a layer, engraved on the layer below where the next ply sits
export function edgeBits(bits, width, height) {
    const edge = new Uint8Array(bits.length);
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const i = y * width + x;
            if (!bits[i]) continue;
            if (
                (x > 0 && !bits[i - 1]) ||
                (x + 1 < width && !bits[i + 1]) ||
                (y > 0 && !bits[i - width]) ||
                (y + 1 < height && !bits[i + width])
            ) {
                edge[i] = 1;
                if (x > 0) edge[i - 1] = 1;
                if (y > 0) edge[i - width] = 1;
            }
        }
    }
    return edge;
}

function engravingFromBits(width, height, bits) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    if (!bits) return canvas;
    const ctx = canvas.getContext('2d');
    const image = ctx.createImageData(width, height);
    for (let i = 0; i < bits.length; i++) {
        if (!bits[i]) continue;
        const o = i * 4;
        image.data[o] = image.data[o + 1] = image.data[o + 2] = image.data[o + 3] = 255;
    }
    ctx.putImageData(image, 0, 0);
    return canvas;
}

// Stroke only. The place outline is engraved, the layers stay the elevation cut.
export function traceRings(ctx, rings, view) {
    if (!rings || !rings.length) return false;
    const { width, height } = ctx.canvas;
    const toPx = project(view, width, height);
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.lineWidth = Math.max(2, Math.round(width / 280));
    ctx.beginPath();
    rings.forEach((ring) => {
        ring.forEach((point, index) => {
            const [px, py] = toPx(point[0], point[1]);
            if (index) ctx.lineTo(px, py);
            else ctx.moveTo(px, py);
        });
    });
    ctx.stroke();
    return true;
}

function boundaryEngraving(width, height, rings, view) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#fff';
    if (!traceRings(ctx, rings, view)) return null;
    return canvas;
}

export async function loadTerrain(view, zoom, token, aspect) {
    if (!token) throw new Error('MAPBOX_TOKEN is not set');
    const width = DEM_WIDTH;
    const height = Math.round(width / aspect);
    const n = 2 ** demZoomFor(view, width);
    const tiles = [];
    for (let x = Math.floor(view.left * n); x <= Math.floor(view.right * n); x++) {
        for (let y = Math.floor(view.top * n); y <= Math.floor(view.bottom * n); y++)
            tiles.push({ z: Math.log2(n), x, y });
    }
    // shortcut: only zoom 6 borrows z7 rivers (~20 tiles); 5.5 and below would need 40+, so they stay river-less
    // (6.5 already rounds to z7 tiles, which carry rivers)
    const rivers =
        zoom === RIVER_ZOOM - 1
            ? loadMapFeatures(view, RIVER_ZOOM, token).then((list) => list.filter((feature) => feature.river))
            : [];
    const [grids, base, extraRivers] = await Promise.all([
        Promise.all(tiles.map((tile) => loadDemTile(tile))),
        loadMapFeatures(view, zoom, token),
        rivers,
    ]);
    const features = base.concat(extraRivers);
    const heights = sampleHeights(tiles, grids, view, width, height);
    const water = paintWater(features, view, width, height);
    let minLand = Infinity;
    let maxLand = -Infinity;
    let minWater = Infinity;
    let maxWater = -Infinity;
    for (let i = 0; i < heights.length; i++) {
        if (water[i]) {
            if (heights[i] < minWater) minWater = heights[i];
            if (heights[i] > maxWater) maxWater = heights[i];
        }
        if (water[i] || heights[i] < 0) continue;
        if (heights[i] < minLand) minLand = heights[i];
        if (heights[i] > maxLand) maxLand = heights[i];
    }
    if (!Number.isFinite(minLand)) minLand = maxLand = 0;
    if (!Number.isFinite(minWater)) minWater = maxWater = 0;
    return { width, height, water, heights, view, zoom, minLand, maxLand, minWater, maxWater };
}

// smoothed px distance to the shore, 0 everywhere when there is no shore in view
function distanceFromLand(water, width, height) {
    if (!water.includes(0)) return new Float32Array(water.length);
    const dist = distanceTo(
        water.map((v) => 1 - v),
        width,
        height
    );
    return blurField(dist, width, height, SHORE_BLUR_PX);
}

function addTerraces(bits, heights, width, height, levels, wanted, minArea, deeper) {
    const used = [];
    levels.forEach((threshold) => {
        if (bits.length >= wanted) return;
        const prev = bits[bits.length - 1];
        const raw = new Uint8Array(prev.length);
        for (let i = 0; i < raw.length; i++) {
            raw[i] = prev[i] && (deeper ? heights[i] <= threshold : heights[i] >= threshold) ? 1 : 0;
        }
        const inside = mergePieces(raw, width, height, minArea, prev);
        if (areaOf(inside) < minArea || areaOf(inside) >= areaOf(prev)) return;
        bits.push(inside);
        used.push(threshold);
    });
    return used;
}

export function drawTopoLayers(terrain, { landLayers, waterStyle, boundary } = {}) {
    const { width, height, heights, minLand, maxLand } = terrain;
    const factor = simplifyFactor(terrain.zoom);
    let land = landBits(terrain.water);
    const landArea = areaOf(land);
    const minArea = Math.max(24, landArea * MIN_LAYER_AREA * factor);
    if (factor > 1) {
        // drop tiny islands; rivers are bridged first so the land between them isn't mistaken for islands
        const bridged = removeSpecks(morph(land, width, height, GAP_PX, true), width, height, minArea);
        land = land.map((v, i) => v & bridged[i]);
        // fill tiny lakes, rivers stay since they connect to bigger water
        land = removeSpecks(
            land.map((v) => 1 - v),
            width,
            height,
            minArea
        ).map((v) => 1 - v);
    }
    const water = land.map((v) => 1 - v);
    const wanted = Math.max(1, landLayers || 10);
    const landBitsList = [land];
    const landUsed = addTerraces(
        landBitsList,
        heights,
        width,
        height,
        // equal height steps keep relief to scale (area quantiles made flat shores look as stepped as mountains)
        levelsFor(minLand, maxLand, wanted - 1),
        wanted,
        minArea,
        false
    );

    const waterBitsList = [];
    if (waterStyle === 'bathymetry') {
        const sea = new Uint8Array(water);
        const seaArea = areaOf(sea);
        const seaMin = Math.max(24, seaArea * MIN_LAYER_AREA * factor);
        if (seaArea >= seaMin) {
            waterBitsList.push(sea);
            const dist = distanceFromLand(water, width, height);
            let maxDist = 0;
            for (let i = 0; i < dist.length; i++) if (water[i] && dist[i] > maxDist) maxDist = dist[i];
            const seaWanted = 4;
            const step = maxDist / seaWanted;
            if (step >= 2) {
                // shallower bands stack on top towards the shore, so the deepest water stays lowest
                const seaLevels = [step * 3, step * 2, step];
                addTerraces(waterBitsList, dist, width, height, seaLevels, seaWanted, seaMin, true);
            }
        }
    }

    const seaMin = Math.max(24, areaOf(water) * MIN_LAYER_AREA * factor);
    // land rests on plain land plies until it is level with the shallowest water ply
    const landLift = Math.max(0, waterBitsList.length - 1);
    const landParts =
        landBitsList.reduce((sum, layer) => sum + countParts(layer, width, height, minArea), 0) +
        landLift * countParts(land, width, height, minArea);
    const waterParts = waterBitsList.reduce((sum, layer) => sum + countParts(layer, width, height, seaMin), 0);
    const outline = boundaryEngraving(width, height, boundary, terrain.view);
    const engrave = (canvas) => {
        if (outline) canvas.getContext('2d').drawImage(outline, 0, 0);
        return canvas;
    };
    return {
        land: landBitsList.map((layer) => maskFromBits(width, height, layer)),
        landEngraving: landBitsList.map((_, index) =>
            engrave(
                engravingFromBits(width, height, landBitsList[index + 1] && edgeBits(landBitsList[index + 1], width, height))
            )
        ),
        water: waterBitsList.map((layer) => maskFromBits(width, height, layer)),
        waterEngraving: waterBitsList.map((_, index) =>
            engrave(
                engravingFromBits(
                    width,
                    height,
                    waterBitsList[index + 1] && edgeBits(waterBitsList[index + 1], width, height)
                )
            )
        ),
        boundary: outline,
        interval: landUsed.length > 1 ? landUsed[1] - landUsed[0] : niceStep(maxLand - minLand, wanted - 1) || 0,
        flat: landUsed.length < 1,
        parts: landParts + waterParts,
        landLift,
        landParts,
        waterParts,
    };
}

// equal height steps from the ground; ply k keeps the buildings at least levels[k] tall
export function cityLevels(heights, plies) {
    const sorted = [...heights].sort((a, b) => a - b);
    const top = sorted.length ? sorted[Math.floor((sorted.length - 1) * CITY_TOP_QUANTILE)] : 0;
    if (!(top > 0) || plies < 1) return { top, levels: [] };
    const step = top / plies;
    return { top, levels: [...Array(plies)].map((_, k) => k * step) };
}

function paintBuildings(buildings, minHeight, view, width, height) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    const toPx = project(view, width, height);
    ctx.fillStyle = '#fff';
    buildings.forEach((feature) => {
        if (feature.building < minHeight) return;
        ctx.beginPath();
        feature.rings.forEach((ring) =>
            ring.forEach((point, index) => {
                const [px, py] = toPx(point[0], point[1]);
                if (index) ctx.lineTo(px, py);
                else ctx.moveTo(px, py);
            })
        );
        ctx.fill('evenodd');
    });
    const pixels = ctx.getImageData(0, 0, width, height).data;
    const bits = new Uint8Array(width * height);
    for (let i = 0; i < bits.length; i++) bits[i] = pixels[i * 4 + 3] > 127 ? 1 : 0;
    return bits;
}

export async function loadCity(view, zoom, token, aspect) {
    const width = DEM_WIDTH;
    const height = Math.round(width / aspect);
    const [features, buildings] = await Promise.all([
        loadMapFeatures(view, zoom, token),
        loadBuildings(view, Math.max(zoom, BUILDING_TILE_ZOOM), token),
    ]);
    const water = paintWater(features, view, width, height);
    return { width, height, water, view, zoom, buildings };
}

// city mode: the base ply is the land, every ply above it keeps the buildings tall enough for it
export function drawCityLayers(city, { landLayers, boundary } = {}) {
    const { width, height, water, buildings, view } = city;
    const land = landBits(water);
    const { top, levels } = cityLevels(
        buildings.map((feature) => feature.building),
        Math.max(2, landLayers || 10) - 1
    );
    const bitsList = [land];
    levels.forEach((level) => {
        const prev = bitsList[bitsList.length - 1];
        // narrow gaps get bridged like on the terrain, so a block of row houses cuts as one piece
        const inside = mergePieces(paintBuildings(buildings, level, view, width, height), width, height, MIN_BUILDING_PX, prev);
        if (areaOf(inside) >= MIN_BUILDING_PX) bitsList.push(inside);
    });
    const outline = boundaryEngraving(width, height, boundary, view);
    const engrave = (canvas) => {
        if (outline) canvas.getContext('2d').drawImage(outline, 0, 0);
        return canvas;
    };
    const landParts = bitsList.reduce((sum, layer) => sum + countParts(layer, width, height, MIN_BUILDING_PX), 0);
    return {
        land: bitsList.map((layer) => maskFromBits(width, height, layer)),
        landEngraving: bitsList.map((_, index) =>
            engrave(engravingFromBits(width, height, bitsList[index + 1] && edgeBits(bitsList[index + 1], width, height)))
        ),
        water: [],
        waterEngraving: [],
        boundary: outline,
        interval: levels.length > 1 ? levels[1] : top,
        maxHeight: top,
        flat: bitsList.length < 2,
        parts: landParts,
        landParts,
        waterParts: 0,
    };
}

// same land/water colours as the terrain locator, buildings in the contour colour
export function drawLocatorCity(dest, city) {
    drawLocatorTopo(dest, { ...city, heights: new Float32Array(city.water.length), minLand: 0, maxLand: 0 });
    const ctx = dest.getContext('2d');
    const toPx = project(city.view, dest.width, dest.height);
    ctx.fillStyle = 'rgb(150, 118, 82)';
    city.buildings.forEach((feature) => {
        ctx.beginPath();
        feature.rings.forEach((ring) =>
            ring.forEach((point, index) => {
                const [px, py] = toPx(point[0], point[1]);
                if (index) ctx.lineTo(px, py);
                else ctx.moveTo(px, py);
            })
        );
        ctx.fill('evenodd');
    });
}

export function drawLocatorTopo(dest, terrain) {
    const w = dest.width;
    const h = dest.height;
    const ctx = dest.getContext('2d');
    const { width, height, water, heights, minLand, maxLand } = terrain;
    const step = niceStep(maxLand - minLand, LOCATOR_LINES);
    const band = (sx, sy) => (step ? Math.floor(heights[sy * width + sx] / step) : 0);
    const image = ctx.createImageData(w, h);
    const data = image.data;
    for (let y = 0; y < h; y++) {
        const sy = Math.min(height - 1, Math.floor((y / h) * height));
        const sy1 = Math.min(height - 1, sy + 1);
        for (let x = 0; x < w; x++) {
            const sx = Math.min(width - 1, Math.floor((x / w) * width));
            const sx1 = Math.min(width - 1, sx + 1);
            const o = (y * w + x) * 4;
            const b = band(sx, sy);
            const line = b !== band(sx1, sy) || b !== band(sx, sy1);
            if (water[sy * width + sx]) {
                data[o] = 30;
                data[o + 1] = 105;
                data[o + 2] = 180;
            } else if (line) {
                data[o] = 150;
                data[o + 1] = 118;
                data[o + 2] = 82;
            } else {
                data[o] = 214;
                data[o + 1] = 184;
                data[o + 2] = 142;
            }
            data[o + 3] = 255;
        }
    }
    ctx.putImageData(image, 0, 0);
}
